import { useEffect, useMemo, useState } from "react";
import { X, Loader2, Copy, AlertTriangle, CheckCircle2, Search } from "lucide-react";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import { API_BASE } from "./constants";
import ModalPortal from "@/components/ui/modal-portal";

const fmt = (n, d = 2) => (n == null || Number.isNaN(Number(n)) ? "—"
  : Number(n).toLocaleString("fr-FR", { maximumFractionDigits: d }));
const frDate = (iso) => (iso && iso.length >= 10 ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");
const norm = (s) => (s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").trim().toLowerCase();
const toNum = (v) => { const n = parseFloat(String(v ?? "").replace(",", ".")); return Number.isNaN(n) ? null : n; };
const perHa = (u) => /\/ha$/i.test(u || "");
const addDays = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const dayDiff = (a, b) => Math.round((new Date(`${b}T12:00:00`) - new Date(`${a}T12:00:00`)) / 86400000);
const TYPE_STYLE = {
  phyto: { bg: "#2D6A4F15", fg: "#2D6A4F" },
  fertilisation: { bg: "#8B5E3C15", fg: "#8B5E3C" },
  biostimulant: { bg: "#0891B215", fg: "#0E7490" },
};
const targetKey = (t) => `${t.parcel_id}|${t.ilot_id || ""}`;

/** Duplication de tout ou partie du cahier vers d'autres parcelles / îlots du compte. */
export default function DuplicateCahierModal({ parcelId, ilotId, onClose }) {
  const [step, setStep] = useState(1);
  const [source, setSource] = useState(null);
  const [targetsData, setTargetsData] = useState(null);
  const [mode, setMode] = useState("total");
  const [selected, setSelected] = useState({});
  const [dateMode, setDateMode] = useState("same");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [chosenTargets, setChosenTargets] = useState({});
  const [query, setQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const q = ilotId ? `&ilot_id=${encodeURIComponent(ilotId)}` : "";
    authFetch(`${API_BASE}/carnet/duplicate/source?parcel_id=${encodeURIComponent(parcelId)}${q}`)
      .then(async (r) => { if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail); return r.json(); })
      .then((d) => { setSource(d); setSelected(Object.fromEntries(d.items.map((it) => [it.key, true]))); })
      .catch((e) => { toast.error(e.message || "Chargement du cahier impossible"); onClose(); });
  }, [parcelId, ilotId, onClose]);

  const loadTargets = (q = "") => {
    authFetch(`${API_BASE}/carnet/duplicate/targets?parcel_id=${encodeURIComponent(parcelId)}${q ? `&q=${encodeURIComponent(q)}` : ""}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setTargetsData)
      .catch(() => toast.error("Chargement des parcelles impossible"));
  };
  useEffect(() => { loadTargets(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [parcelId]);

  // Une ligne par parcelle entière et par îlot, sauf la source elle-même
  const targetRows = useMemo(() => {
    const rows = [];
    (targetsData?.targets || []).forEach((p) => {
      const parcelRow = { parcel_id: p.parcel_id, ilot_id: null, label: p.name, sub: p.commune, surface: p.surface_ha, culture: p.culture, owner: p.locked_by_email };
      if (!(p.parcel_id === parcelId && !ilotId)) rows.push(parcelRow);
      (p.ilots || []).forEach((i) => {
        if (p.parcel_id === parcelId && i.ilot_id === ilotId) return;
        rows.push({ parcel_id: p.parcel_id, ilot_id: i.ilot_id, label: `${p.name} — ${i.name}`, sub: "îlot", surface: i.surface_ha || p.surface_ha, culture: i.culture || p.culture, owner: p.locked_by_email });
      });
    });
    return rows;
  }, [targetsData, parcelId, ilotId]);

  const items = source?.items || [];
  const chosenItems = items.filter((it) => mode === "total" || selected[it.key]);
  const chosenRows = targetRows.filter((t) => chosenTargets[targetKey(t)]);
  const firstDate = chosenItems.find((it) => it.date)?.date;
  const newDate = (d) => (dateMode === "shift" && firstDate && d ? addDays(startDate, dayDiff(firstDate, d)) : d);
  const hasPhyto = chosenItems.some((it) => it.type === "phyto");

  const submit = async () => {
    setCreating(true);
    try {
      const res = await authFetch(`${API_BASE}/carnet/duplicate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source_parcel_id: parcelId,
          source_ilot_id: ilotId || null,
          items: chosenItems.map((it) => it.key),
          targets: chosenRows.map((t) => ({ parcel_id: t.parcel_id, ilot_id: t.ilot_id })),
          date_mode: dateMode,
          start_date: dateMode === "shift" ? startDate : null,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(typeof d.detail === "string" ? d.detail : "Duplication impossible"); return; }
      setResult(d);
      toast.success(`${d.total} brouillon${d.total > 1 ? "s" : ""} créé${d.total > 1 ? "s" : ""}`);
    } catch { toast.error("Erreur de connexion"); }
    finally { setCreating(false); }
  };

  const Seg = ({ value, set, options }) => (
    <div className="inline-flex rounded-lg border border-black/15 overflow-hidden">
      {options.map(([v, l]) => (
        <button key={v} type="button" onClick={() => set(v)} className="px-3 h-8 text-[11.5px] font-semibold"
          style={value === v ? { background: "#2D6A4F", color: "#fff" } : { background: "#fff", color: "#4A4A4A" }}>{l}</button>
      ))}
    </div>
  );

  const stepLabels = ["Saisies à copier", "Parcelles cibles", "Aperçu"];
  const canGo2 = chosenItems.length > 0;
  const canGo3 = canGo2 && chosenRows.length > 0;

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[2200] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="duplicate-cahier-modal">
      <div className="bg-white w-full sm:max-w-2xl max-h-[92dvh] rounded-t-2xl sm:rounded-2xl flex flex-col overflow-hidden shadow-xl">
        <div className="flex items-center gap-2 px-4 py-3 text-white" style={{ background: "#2D6A4F" }}>
          <Copy className="w-4 h-4" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold">Dupliquer le cahier</div>
            {source && <div className="text-[11px] opacity-85 truncate">Source : {source.name}{ilotId ? " (îlot)" : ""} · {source.culture || "—"} · {fmt(source.surface_ha)} ha</div>}
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/15" aria-label="Fermer"><X className="w-4 h-4" /></button>
        </div>

        {!source ? (
          <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
        ) : result ? (
          <div className="p-4 space-y-3 overflow-y-auto" data-testid="duplicate-result">
            <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "#1f5c39" }}>
              <CheckCircle2 className="w-5 h-5" /> {result.total} brouillon{result.total > 1 ? "s" : ""} créé{result.total > 1 ? "s" : ""}
            </div>
            {result.targets.map((t) => (
              <div key={`${t.parcel_id}|${t.ilot_id || ""}`} className="rounded-lg border border-black/10 px-3 py-2 text-[12px]">
                <div className="font-semibold">{t.name}</div>
                <div className="text-gray-500">
                  {t.created.phyto} registre phyto · {t.created.fertilisation} fertilisation · {t.created.biostimulant} biostimulant
                </div>
              </div>
            ))}
            <p className="text-[11.5px] text-gray-500">
              Les copies sont des brouillons, marqués « Copie à vérifier » dans le carnet de chaque parcelle. Vérifiez-les, puis validez les entrées du registre et confirmez les apports.
            </p>
            <div className="flex justify-end"><button onClick={onClose} className="h-9 px-4 rounded-lg text-white text-xs font-semibold" style={{ background: "#2D6A4F" }}>Terminer</button></div>
          </div>
        ) : (
          <>
            <div className="flex gap-1.5 px-4 pt-3">
              {stepLabels.map((l, i) => {
                const n = i + 1;
                const enabled = n === 1 || (n === 2 && canGo2) || (n === 3 && canGo3);
                return (
                  <button key={l} type="button" disabled={!enabled} onClick={() => setStep(n)}
                    className={`flex-1 flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-[11px] ${step === n ? "border-emerald-700 font-semibold text-gray-900" : "border-black/10 text-gray-500"} disabled:opacity-40`}>
                    <span className="w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold shrink-0"
                      style={step === n ? { background: "#2D6A4F", color: "#fff" } : { background: "#0000000D" }}>{n}</span>
                    <span className="truncate">{l}</span>
                  </button>
                );
              })}
            </div>

            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              {step === 1 && (
                items.length === 0 ? (
                  <p className="text-xs italic text-gray-500 py-6 text-center">Aucune saisie à dupliquer (registre phyto, fertilisation ou biostimulants).</p>
                ) : (
                  <>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Seg value={mode} set={setMode} options={[["total", "Cahier complet"], ["partiel", "Sélection"]]} />
                      <span className="text-[11px] text-gray-500">{chosenItems.length} saisie{chosenItems.length > 1 ? "s" : ""} sur {items.length}, dans l'ordre chronologique</span>
                    </div>
                    <div className="space-y-1.5">
                      {items.map((it) => {
                        const on = mode === "total" || !!selected[it.key];
                        const st = TYPE_STYLE[it.type];
                        return (
                          <label key={it.key} className={`flex gap-2.5 items-start rounded-lg border border-black/10 px-3 py-2 cursor-pointer ${on ? "" : "opacity-55"}`}>
                            <input type="checkbox" className="mt-1 accent-emerald-700" checked={on} disabled={mode === "total"}
                              onChange={(e) => setSelected((s) => ({ ...s, [it.key]: e.target.checked }))} />
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center gap-1.5 flex-wrap text-[12px] font-semibold">
                                {frDate(it.date)}
                                <span className="text-[10px] font-semibold rounded-full px-1.5 py-0.5" style={{ background: st.bg, color: st.fg }}>{it.label}</span>
                                {it.status === "draft" && <span className="text-[10px] text-gray-400 font-normal">brouillon</span>}
                              </span>
                              <span className="block text-[11.5px] text-gray-600">
                                {it.products.map((p) => `${p.name}${p.dose != null && p.dose !== "" ? ` ${fmt(toNum(p.dose), 3)} ${p.unit || ""}` : ""}`).join(" + ")}
                                {it.bouillie_l_ha ? ` · bouillie ${fmt(toNum(it.bouillie_l_ha), 0)} L/ha` : ""}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap pt-1">
                      <span className="text-[12px] text-gray-600">Dates des copies :</span>
                      <Seg value={dateMode} set={setDateMode} options={[["same", "Mêmes dates"], ["shift", "Décaler"]]} />
                      {dateMode === "shift" && (
                        <label className="flex items-center gap-1.5 text-[12px]">1<sup>re</sup> saisie le
                          <input type="date" value={startDate} onChange={(e) => e.target.value && setStartDate(e.target.value)}
                            className="h-8 border border-black/15 rounded-md px-2 text-[12px]" />
                        </label>
                      )}
                    </div>
                    {dateMode === "shift" && <p className="text-[11px] italic text-gray-500">Les écarts entre saisies sont conservés : le calendrier est simplement translaté.</p>}
                  </>
                )
              )}

              {step === 2 && (
                <>
                  {targetsData?.is_admin && (
                    <div className="flex gap-1.5">
                      <div className="relative flex-1">
                        <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-gray-400" />
                        <input value={query} onChange={(e) => setQuery(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") loadTargets(query); }}
                          placeholder="Admin : chercher une parcelle (nom, identifiant, commune)"
                          className="w-full h-8 border border-black/15 rounded-md pl-8 pr-2 text-[12px]" />
                      </div>
                      <button type="button" onClick={() => loadTargets(query)} className="h-8 px-3 rounded-md border border-black/15 text-[11.5px] font-semibold">Chercher</button>
                    </div>
                  )}
                  {!targetsData ? (
                    <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
                  ) : targetRows.length === 0 ? (
                    <p className="text-xs italic text-gray-500 py-6 text-center">
                      Aucune autre parcelle ou îlot verrouillé à votre nom. La duplication de cahier sert aux comptes qui gèrent plusieurs parcelles.
                    </p>
                  ) : (
                    <div className="space-y-1.5">
                      {targetRows.map((t) => {
                        const k = targetKey(t);
                        const diff = hasPhyto && source.culture && t.culture && norm(t.culture) !== norm(source.culture);
                        return (
                          <label key={k} className={`flex gap-2.5 items-start rounded-lg border px-3 py-2 cursor-pointer ${chosenTargets[k] ? "border-emerald-700 bg-emerald-50/50" : "border-black/10"}`}>
                            <input type="checkbox" className="mt-1 accent-emerald-700" checked={!!chosenTargets[k]}
                              onChange={(e) => setChosenTargets((s) => ({ ...s, [k]: e.target.checked }))} />
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center gap-1.5 flex-wrap text-[12px] font-semibold">
                                {t.label}
                                {diff && <span className="text-[10px] rounded-full px-1.5 py-0.5" style={{ background: "#FBF1DC", color: "#9A6A12" }}>Culture différente</span>}
                              </span>
                              <span className="block text-[11.5px] text-gray-600">
                                {[t.sub, t.culture || "culture non renseignée", `${fmt(t.surface)} ha`].filter(Boolean).join(" · ")}
                                {targetsData.is_admin && t.owner ? ` · ${t.owner}` : ""}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  {chosenRows.map((t) => {
                    const diff = hasPhyto && source.culture && t.culture && norm(t.culture) !== norm(source.culture);
                    return (
                      <div key={targetKey(t)} className="space-y-1.5">
                        <div className="flex items-baseline justify-between gap-2 flex-wrap">
                          <h4 className="text-[13px] font-bold">{t.label}</h4>
                          <span className="text-[11px] text-gray-500">{t.culture || "—"} · {fmt(t.surface)} ha (source {fmt(source.surface_ha)} ha)</span>
                        </div>
                        <div className="overflow-x-auto rounded-lg border border-black/10">
                          <table className="w-full min-w-[520px] text-[11.5px]">
                            <thead>
                              <tr className="bg-black/[0.03] text-[10px] uppercase tracking-wide text-gray-500">
                                <th className="text-left px-2 py-1.5">Date</th><th className="text-left px-2 py-1.5">Produit</th>
                                <th className="text-right px-2 py-1.5">Dose (inchangée)</th><th className="text-right px-2 py-1.5">Quantité totale</th>
                              </tr>
                            </thead>
                            <tbody>
                              {chosenItems.map((it) => (
                                it.products.map((p, i) => {
                                  const d = toNum(p.dose);
                                  const base = (p.unit || "").split("/")[0];
                                  const scalable = d != null && perHa(p.unit) && t.surface;
                                  return (
                                    <tr key={`${it.key}-${i}`} className="border-t border-black/5">
                                      <td className="px-2 py-1.5 whitespace-nowrap">{i === 0 ? frDate(newDate(it.date)) : ""}</td>
                                      <td className="px-2 py-1.5">{p.name}{i === 0 && it.products.length > 1 ? <span className="text-gray-400"> (mélange)</span> : ""}</td>
                                      <td className="px-2 py-1.5 text-right whitespace-nowrap">{d != null ? `${fmt(d, 3)} ${p.unit || ""}` : "—"}</td>
                                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                                        {scalable ? <>{fmt(d * source.surface_ha)} {base} → <b style={{ color: "#1f5c39" }}>{fmt(d * t.surface)} {base}</b></> : "—"}
                                      </td>
                                    </tr>
                                  );
                                }).concat(it.bouillie_l_ha && t.surface ? [(
                                  <tr key={`${it.key}-b`} className="border-t border-black/5 text-gray-500">
                                    <td /><td className="px-2 py-1.5">Bouillie</td>
                                    <td className="px-2 py-1.5 text-right">{fmt(toNum(it.bouillie_l_ha), 0)} L/ha</td>
                                    <td className="px-2 py-1.5 text-right">{fmt(toNum(it.bouillie_l_ha) * source.surface_ha, 0)} L → <b style={{ color: "#1f5c39" }}>{fmt(toNum(it.bouillie_l_ha) * t.surface, 0)} L</b></td>
                                  </tr>
                                )] : [])
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {diff && (
                          <p className="flex gap-1.5 text-[11.5px] rounded-lg px-2.5 py-2" style={{ background: "#FBF1DC", color: "#9A6A12" }}>
                            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                            Culture différente ({t.culture}) : l'autorisation des produits phyto est recontrôlée sur chaque brouillon. Un produit non autorisé sur cette culture bloquera la validation.
                          </p>
                        )}
                      </div>
                    );
                  })}
                  <p className="text-[11.5px] text-gray-500">
                    {chosenItems.length * chosenRows.length} saisie{chosenItems.length * chosenRows.length > 1 ? "s" : ""} seront copiées
                    ({chosenItems.length} × {chosenRows.length} cible{chosenRows.length > 1 ? "s" : ""}), en brouillon. Rien n'est verrouillé.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-between gap-2 px-4 py-3 border-t border-black/5">
              <button type="button" onClick={() => (step === 1 ? onClose() : setStep(step - 1))}
                className="h-9 px-4 rounded-lg border border-black/15 text-xs font-semibold">{step === 1 ? "Annuler" : "Retour"}</button>
              {step < 3 ? (
                <button type="button" disabled={step === 1 ? !canGo2 : !canGo3} onClick={() => setStep(step + 1)}
                  className="h-9 px-4 rounded-lg text-white text-xs font-semibold disabled:opacity-40" style={{ background: "#2D6A4F" }}>
                  {step === 1 ? "Choisir les parcelles" : "Voir l'aperçu"}
                </button>
              ) : (
                <button type="button" disabled={creating} onClick={submit} data-testid="duplicate-submit"
                  className="h-9 px-4 rounded-lg text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" style={{ background: "#2D6A4F" }}>
                  {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />} Créer les brouillons
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
    </ModalPortal>
  );
}
